import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OpponentGrid from '../components/OpponentGrid';
import OpponentDetailsOverlay from '../components/OpponentDetailsOverlay';
import { handleAuthError } from '../utils/auth';

const API_BASE_URL = 'https://best3intennis.com/best3intennis-api/api';

const OpponentPage = () => {
  const [opponents, setOpponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const navigate = useNavigate();

  const fetchOpponents = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError(navigate);
        return;
      }
      const response = await fetch(`${API_BASE_URL}/get_opponents.php`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
            handleAuthError(navigate);
            return;
        }
        throw new Error(result?.message || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log("Fetched opponents:", data); // Debugging: Log fetched data
      setOpponents(data);
    } catch (error) {
      console.error("Error fetching opponents:", error);
      if (error.message.includes('token')) {
        handleAuthError(navigate);
      } else {
        setError("Failed to load opponents.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Add a state to trigger re-fetching
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetchOpponents();
  }, [refreshKey]); // Re-fetch whenever refreshKey changes

  const handleAddOpponent = () => {
    setSelectedOpponent(null); // Clear selected opponent for add mode
    setIsOverlayOpen(true);
  };

  const handleSelectOpponent = async (opponent) => {
    // Fetch full details for the selected opponent
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError(navigate);
        return;
      }
      const response = await fetch(`${API_BASE_URL}/get_opponent_details.php?id=${opponent.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
            handleAuthError(navigate);
            return;
        }
        throw new Error(result?.message || `HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSelectedOpponent(data);
      setIsOverlayOpen(true);
    } catch (error) {
      console.error("Error fetching opponent details:", error);
      if (error.message.includes('token')) {
        handleAuthError(navigate);
      } else {
        setError("Failed to load opponent details.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseOverlay = () => {
    setIsOverlayOpen(false);
    setSelectedOpponent(null);
  };

  const handleSaveOpponent = async (opponentData) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError(navigate);
        return;
      }
      const endpoint = opponentData.id ? 'update_opponent.php' : 'add_opponent.php';
      const method = 'POST'; // Both add and update use POST

      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(opponentData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        if (errorData && (errorData.message?.includes('token') || errorData.error?.includes('token'))) {
            handleAuthError(navigate);
            return;
        }
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }
      
      console.log("Opponent saved, triggering refresh..."); // Debugging: Log refresh trigger
      setRefreshKey(prevKey => prevKey + 1); // Increment key to trigger useEffect
      handleCloseOverlay(); // Close overlay only on successful save
    } catch (error) {
      console.error("Error saving opponent:", error);
      if (error.message.includes('token')) {
        handleAuthError(navigate);
      } else {
        setError(`Failed to save opponent: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center p-4">Loading opponents...</div>;
  if (error) return <div className="text-center p-4 text-red-500">{error}</div>;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Opponents</h1>
      <OpponentGrid
        opponents={opponents} // Pass fetched opponents to the grid
        onAddOpponent={handleAddOpponent}
        onSelectOpponent={handleSelectOpponent}
      />

      {isOverlayOpen && (
        <OpponentDetailsOverlay
          opponent={selectedOpponent}
          onClose={handleCloseOverlay}
          onSave={handleSaveOpponent}
        />
      )}
    </div>
  );
};

export default OpponentPage;


